/**
 * Dashboard Profile Card
 * Read-only display of the user's own profile on the dashboard
 * Identical layout for both owner and helper
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, helpersAPI } from '../../services/api';
import {
  Star,
  Phone,
  Globe,
  Download,
  FileText,
  Clock,
  CheckCircle,
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function DashboardProfileCard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const isHelper = user?.user_type === 'helper';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises = [
        usersAPI.getProfile(),
        usersAPI.getMyReviews(),
        usersAPI.getMyStats(),
      ];

      if (isHelper) {
        promises.push(helpersAPI.getDocuments());
      }

      const results = await Promise.all(promises);

      setProfile(results[0].data);
      setReviews(results[1].data || []);
      setStats(results[2].data);
      if (isHelper && results[3]) {
        setDocuments(results[3].data || []);
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${UPLOADS_BASE_URL}${url}`;
  };

  const formatDocType = (type) => {
    const labels = {
      cv: 'CV / Resume',
      reference: 'Reference Letter',
      recommendation: 'Recommendation',
      stcw95: 'STCW 95',
      certification: 'Certification',
      id_document: 'ID',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const renderStars = (rating) => {
    const stars = [];
    const rounded = Math.round(rating);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`h-4 w-4 ${i <= rounded ? 'text-gold-500 fill-current' : 'text-gray-300'}`}
        />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="spinner w-6 h-6" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const helperProfile = profile.helper_profile;
  const phone = isHelper
    ? (helperProfile?.phone || helperProfile?.whatsapp || profile.phone || user?.phone)
    : (profile.phone || user?.phone);
  const languages = isHelper ? (helperProfile?.languages || []) : [];
  const experience = isHelper ? (helperProfile?.experience_years || 0) : null;
  const photoUrl = getPhotoUrl(
    profile.profile_photo_url ||
    helperProfile?.profile_photo_url ||
    user?.profile_photo_url
  );

  // Filter documents: only show CV and references for download
  const downloadableDocs = documents.filter(d =>
    ['cv', 'reference', 'recommendation'].includes(d.document_type)
  );

  return (
    <div className="card">
      <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
        My Profile
      </h2>

      {/* Header: Photo + Name + Basic Info */}
      <div className="flex items-start gap-5 mb-6">
        <div className="h-20 w-20 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ring-4 ring-primary-50">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-primary-700 font-bold text-2xl">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </span>
          )}
        </div>
        <div className="min-w-0 pt-1">
          <p className="text-xl font-semibold text-navy-900">
            {profile.first_name} {profile.last_name}
          </p>
          <p className="text-sm text-navy-500 capitalize">
            {profile.user_type?.replace('_', ' ')}
          </p>

          {phone && (
            <p className="flex items-center text-sm text-navy-600 mt-2">
              <Phone className="h-4 w-4 mr-2 flex-shrink-0 text-navy-400" />
              {phone}
            </p>
          )}

          {experience !== null && experience > 0 && (
            <p className="flex items-center text-sm text-navy-600 mt-1.5">
              <Clock className="h-4 w-4 mr-2 flex-shrink-0 text-navy-400" />
              {experience} year{experience !== 1 ? 's' : ''} experience
            </p>
          )}

          {languages.length > 0 && (
            <p className="flex items-center text-sm text-navy-600 mt-1.5">
              <Globe className="h-4 w-4 mr-2 flex-shrink-0 text-navy-400" />
              {languages.join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center mb-1">
              <CheckCircle className="h-5 w-5 text-green-500 mr-1.5" />
              <span className="text-2xl font-bold text-navy-900">{stats.completed_jobs}</span>
            </div>
            <p className="text-xs text-navy-500">Completed Jobs</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center mb-1">
              <Star className="h-5 w-5 text-gold-500 mr-1.5" />
              <span className="text-2xl font-bold text-navy-900">
                {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '--'}
              </span>
            </div>
            <p className="text-xs text-navy-500">
              Rating{stats.rating_count > 0 ? ` (${stats.rating_count})` : ''}
            </p>
          </div>
        </div>
      )}

      {/* Documents - Helper only */}
      {isHelper && downloadableDocs.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-navy-700 mb-3 flex items-center">
            <FileText className="h-4 w-4 mr-1.5" />
            Documents
          </h3>
          <div className="space-y-2">
            {downloadableDocs.map((doc) => (
              <a
                key={doc.id}
                href={`${UPLOADS_BASE_URL}${doc.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
              >
                <div className="flex items-center min-w-0">
                  <div className="h-9 w-9 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="h-4 w-4 text-primary-600" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="text-sm font-medium text-navy-900 truncate">{doc.file_name}</p>
                    <span className="text-xs text-primary-600">{formatDocType(doc.document_type)}</span>
                  </div>
                </div>
                <span className="flex items-center text-sm text-primary-600 group-hover:text-primary-700 ml-3 flex-shrink-0">
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      <div>
        <h3 className="text-sm font-semibold text-navy-700 mb-3 flex items-center">
          <Star className="h-4 w-4 mr-1.5" />
          Reviews
        </h3>
        {reviews.length === 0 ? (
          <p className="text-sm text-navy-400 italic py-4">No reviews yet</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((review, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-0.5">
                    {renderStars(review.rating)}
                  </div>
                  <span className="text-xs text-navy-400">
                    {format(new Date(review.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
                <p className="text-sm text-navy-700 italic leading-relaxed">"{review.comment}"</p>
                <p className="text-xs text-navy-500 mt-2">
                  -- {review.reviewer_first_name} {review.reviewer_last_name}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardProfileCard;
